/**
 * Conservative presence/absence evidence for the site's wetlands boolean.
 * Permitting, impacts, off-site routing and unrelated environmental negations
 * do not establish whether wetlands are present. Conflicting or conditional
 * statements remain unknown; this helper never derives an acreage.
 */
export function explicitWetlandPresence(text: string): boolean | null {
  const signals = new Set<boolean>();
  const clauses = text.toLowerCase().split(/(?<=[.!?])\s+|[\r\n;]+|\b(?:but|however|whereas)\b/);
  for (const clause of clauses) {
    if (!/\bwetlands?\b/.test(clause)) continue;
    if (/\?|\b(?:may|might|could|potentially|potential|possible|possibly|unknown|unclear|undetermined|whether|pending)\b|\bnot\s+(?:yet\s+)?(?:known|determined)\b/.test(clause)) continue;
    if (/\boff[ -]?site\b|\badjacent\b|\boutside (?:the )?(?:site|parcel|property)\b/.test(clause)) continue;

    // "No wetland delineation/impacts" negates an activity or effect, not
    // wetlands themselves. Keep the negator attached to the exact predicate.
    const absent = /\b(?:no|zero|without|absence of)\s+(?:(?:known|jurisdictional|mapped|regulated|forested|wooded)\s+)*wetlands?\b(?!\s+(?:delineation|surveys?|stud(?:y|ies)|assessment|permits?|mitigation|impacts?|issues?|constraints?|work)\b)/.test(clause)
      || /\bwetlands?\s+(?:(?:are|were|is|was|have been)\s+)?(?:absent|not\s+(?:present|found|identified|detected|observed))\b/.test(clause);
    if (absent) { signals.add(false); continue; }
    const present = /\bwetlands?\s+(?:(?:are|were|is|was|have been)\s+)?(?:present|found|identified|detected|observed|mapped|located|delineated|occur|exist)\b/.test(clause)
      || /\b(?:site|parcel|property|tract)\s+(?:has|contains|supports|includes)\s+(?:(?:jurisdictional|mapped|regulated|forested|wooded)\s+)*wetlands?\b/.test(clause);
    if (present) signals.add(true);
  }
  return signals.size === 1 ? [...signals][0]! : null;
}
